import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import styles from './JobManagement.module.css';
import { jobApi, Job, JobFormData } from '../../services/jobApi';

interface JobFormState {
  name: string;
  function_name: string;
  description: string;
  cron_expression: string;
  is_active: boolean;
}

const JobManagement: React.FC = () => {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [editingJob, setEditingJob] = useState<Job | null>(null);
  const [formData, setFormData] = useState<JobFormState>({
    name: '',
    function_name: '',
    description: '',
    cron_expression: '',
    is_active: true
  });
  const isMountedRef = useRef<boolean>(true);

  // Function to translate cron expressions to human-readable English
  const translateCronExpression = (cronExpression: string): string => {
    if (!cronExpression) return '-';

    const parts = cronExpression.trim().split(/\s+/);
    if (parts.length !== 5) return cronExpression;

    const [minute, hour, day, month, weekday] = parts;

    // Handle special cases first
    if (minute.startsWith('*/')) {
      const interval = minute.substring(2);
      if (hour === '*' && day === '*' && month === '*' && weekday === '*') {
        return `every ${interval} minutes`;
      }
    }

    if (hour.startsWith('*/')) {
      const interval = hour.substring(2);
      if (minute === '0' && day === '*' && month === '*' && weekday === '*') {
        return `every ${interval} hours`;
      }
    }

    // Daily schedules
    if (day === '*' && month === '*' && weekday === '*' && !minute.includes('*') && !hour.includes('*')) {
      return `daily at ${hour.padStart(2, '0')}:${minute.padStart(2, '0')}`;
    }

    // Weekly schedules
    if (day === '*' && month === '*' && !weekday.includes('*') && !minute.includes('*') && !hour.includes('*')) {
      const weekdays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      const dayName = weekdays[parseInt(weekday)] || weekday;
      return `${dayName}s at ${hour.padStart(2, '0')}:${minute.padStart(2, '0')}`;
    }

    // Monthly schedules
    if (!day.includes('*') && month === '*' && weekday === '*' && !minute.includes('*') && !hour.includes('*')) {
      const suffix = getOrdinalSuffix(parseInt(day));
      return `monthly on ${day}${suffix} at ${hour.padStart(2, '0')}:${minute.padStart(2, '0')}`;
    }

    // Hourly schedules
    if (!minute.includes('*') && hour === '*' && day === '*' && month === '*' && weekday === '*') {
      return `hourly at minute ${minute}`;
    }

    // If we can't translate, return the original expression
    return cronExpression;
  };

  // Helper function to get ordinal suffix (1st, 2nd, 3rd, etc.)
  const getOrdinalSuffix = (num: number): string => {
    const j = num % 10;
    const k = num % 100;
    if (j === 1 && k !== 11) return 'st';
    if (j === 2 && k !== 12) return 'nd';
    if (j === 3 && k !== 13) return 'rd';
    return 'th';
  };

  useEffect(() => {
    loadJobs();
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const loadJobs = async () => {
    try {
      setLoading(true);
      const data = await jobApi.getAllJobs();
      if (isMountedRef.current) {
        // Sort jobs by cron_expression in ascending order
        const sortedJobs = data.sort((a, b) => {
          const cronA = a.cron_expression || '';
          const cronB = b.cron_expression || '';
          return cronA.localeCompare(cronB);
        });
        setJobs(sortedJobs);
      }
    } catch (error) {
      console.error('Error loading jobs:', error);
      toast.error('Failed to load jobs. Please try again later.', {
        position: "top-right",
        autoClose: 5000
      });
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target as HTMLInputElement;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const openNewJobModal = () => {
    setEditingJob(null);
    setFormData({
      name: '',
      function_name: '',
      description: '',
      cron_expression: '',
      is_active: true
    });
    setShowModal(true);
  };

  const openEditModal = (job: Job) => {
    setEditingJob(job);
    setFormData({
      name: job.name,
      function_name: job.function_name,
      description: job.description || '',
      cron_expression: job.cron_expression || '',
      is_active: job.is_active
    });
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (editingJob) {
        await jobApi.updateJob(editingJob.id, formData);
        toast.success('Job updated successfully', {
          position: "top-right",
          autoClose: 3000
        });
      } else {
        await jobApi.createJob(formData);
        toast.success('Job created successfully', {
          position: "top-right",
          autoClose: 3000
        });
      }

      setShowModal(false);
      loadJobs();
    } catch (error) {
      console.error('Error saving job:', error);
      toast.error(`Failed to ${editingJob ? 'update' : 'create'} job. Please try again.`, {
        position: "top-right",
        autoClose: 5000
      });
    }
  };

  const handleDelete = async (jobId: string) => {
    toast.warn(
      <div style={{ minWidth: '300px' }}>
        <p className="mb-3 fw-bold">Are you sure you want to delete this job?</p>
        <div className="d-flex justify-content-center gap-3">
          <button 
            className="btn btn-secondary px-4" 
            onClick={() => toast.dismiss()}
          >
            Cancel
          </button>
          <button 
            className="btn btn-danger px-4" 
            onClick={async () => {
              toast.dismiss();
              try {
                await jobApi.deleteJob(jobId);
                toast.success('Job deleted successfully', {
                  position: "top-right",
                  autoClose: 3000
                });
                loadJobs();
              } catch (error) {
                console.error('Error deleting job:', error);
                toast.error('Failed to delete job. Please try again.', {
                  position: "top-right",
                  autoClose: 5000
                });
              }
            }}
          >
            Delete
          </button>
        </div>
      </div>,
      {
        position: "top-right",
        autoClose: false,
        hideProgressBar: true,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        closeButton: false,
      }
    );
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingJob(null);
  };

  if (loading) {
    return (
      <div className={`container mt-5 ${styles.loadingContainer}`}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <div className="mt-2">Loading jobs...</div>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <div className="container mt-4">
        <nav aria-label="breadcrumb">
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/admin"><i className="bi bi-house-door me-1"></i>Admin</Link>
            </li>
            <li className="breadcrumb-item active" aria-current="page">Job Management</li>
          </ol>
        </nav>

        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h2 className="mb-1">Job Management</h2>
            <p className="text-muted mb-0">
              <i className="bi bi-gear me-2"></i>
              Configure and manage scheduled jobs
            </p>
          </div>
          <div className="d-flex gap-2">
            <button 
              className="btn btn-outline-primary btn-sm"
              onClick={loadJobs}
              title="Refresh jobs"
            >
              <i className="bi bi-arrow-clockwise me-1"></i>
              Refresh
            </button>
            <button 
              className="btn btn-primary" 
              onClick={openNewJobModal}
            >
              <i className="bi bi-plus-circle me-2"></i>
              New Job
            </button>
          </div>
        </div>

        <div className={styles.tableContainer}>
          <div className="d-flex justify-content-between align-items-center p-3 bg-light border-bottom">
            <div className="d-flex align-items-center">
              <i className="bi bi-table me-2 text-primary"></i>
              <h5 className="mb-0">Scheduled Jobs</h5>
            </div>
            <span className="badge bg-primary rounded-pill">
              {jobs.length} {jobs.length === 1 ? 'Job' : 'Jobs'}
            </span>
          </div>
          
          <table className={`table ${styles.jobTable}`}>
            <thead>
              <tr>
                <th>
                  <i className="bi bi-tag me-1"></i>
                  Name
                </th>
                <th>
                  <i className="bi bi-code-square me-1"></i>
                  Function
                </th>
                <th>
                  <i className="bi bi-clock me-1"></i>
                  Cron
                </th>
                <th>
                  <i className="bi bi-calendar-event me-1"></i>
                  Schedule
                </th>
                <th>
                  <i className="bi bi-toggle-on me-1"></i>
                  Status
                </th>
                <th>
                  <i className="bi bi-tools me-1"></i>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {jobs.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center p-5">
                    <div className={styles.emptyState}>
                      <i className="bi bi-inbox display-4 text-muted mb-3"></i>
                      <h5 className="text-muted">No jobs configured</h5>
                      <p className="text-muted mb-3">
                        Create your first scheduled job to get started with automation.
                      </p>
                      <button 
                        className="btn btn-primary"
                        onClick={openNewJobModal}
                      >
                        <i className="bi bi-plus-circle me-2"></i>
                        Create First Job
                      </button>
                    </div>
                  </td>
                </tr>
              ) : (
                jobs.map((job, index) => (
                  <tr key={job.id} className={index % 2 === 0 ? styles.evenRow : styles.oddRow}>
                    <td className={styles.jobTableCell}>
                      <div className={styles.jobName}>
                        <i className="bi bi-briefcase me-2 text-primary"></i>
                        <span className="fw-medium">{job.name}</span>
                      </div>
                      {job.description && (
                        <small className="text-muted d-block mt-1">
                          {job.description}
                        </small>
                      )}
                    </td>
                    <td className={styles.jobTableCell}>
                      <span className={styles.functionName}>{job.function_name}</span>
                    </td>
                    <td className={styles.jobTableCell}>
                      {job.cron_expression ? (
                        <span className={styles.cronExpression}>{job.cron_expression}</span>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td className={styles.jobTableCell}>
                      <span className="text-primary fw-medium">
                        <i className="bi bi-calendar-check me-1"></i>
                        {translateCronExpression(job.cron_expression || '')}
                      </span>
                    </td>
                    <td className={styles.jobTableCell}>
                      <span className={`badge ${job.is_active ? 'bg-success' : 'bg-secondary'} ${styles.statusBadge}`}>
                        {job.is_active ? (
                          <>
                            <i className="bi bi-check-circle me-1"></i>
                            Active
                          </>
                        ) : (
                          <>
                            <i className="bi bi-pause-circle me-1"></i>
                            Inactive
                          </>
                        )}
                      </span>
                    </td>
                    <td className={styles.jobTableCell}>
                      <div className={`btn-group ${styles.actionButtons}`}>
                        <button 
                          className="btn btn-sm btn-outline-warning"
                          onClick={() => openEditModal(job)}
                          title="Edit Job"
                        >
                          <i className="bi bi-pencil-fill"></i>
                        </button>
                        <Link 
                          to="/admin/job-logs" 
                          className="btn btn-sm btn-outline-info"
                          title="View Logs"
                        >
                          <i className="bi bi-file-text"></i>
                        </Link>
                        <button 
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleDelete(job.id)}
                          title="Delete Job"
                        >
                          <i className="bi bi-trash-fill"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Modal */}
        {showModal && (
          <div className="modal fade show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-lg">
              <div className={`modal-content ${styles.modalContent}`}>
                <div className="modal-header">
                  <h5 className="modal-title">
                    <i className={`bi ${editingJob ? 'bi-pencil-square' : 'bi-plus-circle'} me-2`}></i>
                    {editingJob ? 'Edit Scheduled Job' : 'Create New Scheduled Job'}
                  </h5>
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={closeModal}
                  ></button>
                </div>
                <form onSubmit={handleSubmit}>
                  <div className="modal-body">
                    <div className="row">
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label htmlFor="name" className="form-label">
                            <i className="bi bi-tag me-1"></i>
                            Job Name
                          </label>
                          <input 
                            type="text" 
                            className="form-control" 
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            placeholder="Enter a descriptive name"
                            required
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label htmlFor="function_name" className="form-label">
                            <i className="bi bi-code-square me-1"></i>
                            Function Name
                          </label>
                          <input 
                            type="text" 
                            className="form-control" 
                            id="function_name"
                            name="function_name"
                            value={formData.function_name}
                            onChange={handleInputChange}
                            placeholder="e.g., process_data"
                            required
                          />
                        </div>
                      </div>
                    </div>
                    
                    <div className="mb-3">
                      <label htmlFor="description" className="form-label">
                        <i className="bi bi-info-circle me-1"></i>
                        Description
                      </label>
                      <textarea 
                        className="form-control" 
                        id="description"
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        rows={3}
                        placeholder="Describe what this job does..."
                      />
                    </div>
                    
                    <div className="mb-3">
                      <label htmlFor="cron_expression" className="form-label">
                        <i className="bi bi-clock me-1"></i>
                        Cron Expression
                      </label>
                      <input 
                        type="text" 
                        className="form-control" 
                        id="cron_expression"
                        name="cron_expression"
                        value={formData.cron_expression}
                        onChange={handleInputChange}
                        placeholder="* * * * *"
                        title="Cron expression: minute hour day month weekday"
                      />
                      <div className="form-text">
                        <div className="d-flex flex-wrap gap-3 mt-2">
                          <span><strong>Format:</strong> <code>min hour day month weekday</code></span>
                          <span><strong>Beispiele:</strong> <code>0 9 * * 1</code> (Montags um 9:00), <code>*/5 * * * *</code> (alle 5 Min)</span>
                        </div>
                        <div className="mt-2">
                          <i className="bi bi-lightbulb me-1 text-warning"></i>
                          <a href="https://crontab.guru/" target="_blank" rel="noopener noreferrer">
                            Verwende crontab.guru für Hilfe beim Erstellen von Cron-Expressions
                          </a>
                        </div>
                      </div>
                    </div>
                    
                    <div className={`form-check ${styles.checkboxContainer}`}>
                      <input 
                        className="form-check-input" 
                        type="checkbox" 
                        id="is_active"
                        name="is_active"
                        checked={formData.is_active}
                        onChange={handleInputChange}
                      />
                      <label className="form-check-label" htmlFor="is_active">
                        <i className="bi bi-toggle-on me-1"></i>
                        <strong>Active Job</strong>
                        <small className="d-block text-muted">Enable this job to run according to the schedule</small>
                      </label>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={closeModal}>
                      <i className="bi bi-x-circle me-1"></i>
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-success">
                      <i className={`bi ${editingJob ? 'bi-check-circle' : 'bi-plus-circle'} me-1`}></i>
                      {editingJob ? 'Update Job' : 'Create Job'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>

      <ToastContainer 
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={true}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss={false}
        draggable
        pauseOnHover
        theme="light"
        style={{ zIndex: 9999 }}
        limit={3}
      />
    </div>
  );
};

export default JobManagement;
